import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import type { DataResponse } from "@/api/schemas";

/**
 * Správa používateľov — celé je to admin-only.
 *
 * Pri vypnutej autentifikácii backend vracia **409**, nie 401, aby UI
 * mohlo povedať, čo treba na serveri prepnúť; `RequireAuth` by na 401
 * poslal na login, ktorý pri `AUTH_ENABLED=false` odmieta prihlásiť
 * kohokoľvek.
 */

/** Rola ne-admina (KOLO 231) — admin má všetky práva bez ohľadu na ňu. */
export type Rola = "rozpoctar" | "obchodnik";

export interface AppUser {
  id: string;
  username: string;
  meno: string;
  je_admin: boolean;
  je_aktivny: boolean;
  rola?: Rola;
}

export interface UserCreate {
  username: string;
  meno: string;
  heslo: string;
  je_admin?: boolean;
  rola?: Rola;
}

export interface UserUpdate {
  meno?: string;
  je_admin?: boolean;
  je_aktivny?: boolean;
  nove_heslo?: string;
  rola?: Rola;
}

export function useUsers(
  enabled = true,
): UseQueryResult<DataResponse<AppUser[]>> {
  return useQuery({
    queryKey: queryKeys.users.list(),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<AppUser[]>>("/users");
      return data;
    },
    enabled,
    // 409 (vypnutá auth) ani 403 sa opakovaním nezmenia
    retry: false,
  });
}

export function useCreateUser(): UseMutationResult<
  DataResponse<AppUser>,
  Error,
  UserCreate
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UserCreate) => {
      const { data } = await api.post<DataResponse<AppUser>>("/users", payload);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}

export function useUpdateUser(): UseMutationResult<
  DataResponse<AppUser>,
  Error,
  { id: string; data: UserUpdate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await api.patch<DataResponse<AppUser>>(`/users/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}
